// Форма виджета: допустимые визуализации, разрезы, периоды и зависимые поля.

import type { Indicator, IndicatorDimension, VisualizationType } from '../types';
import { api } from '../api';
import { formatNumber as formatLocalizedNumber, t } from '../i18n';
import { isSnapshotVisualization } from '../data-utils';

import { CARTESIAN_BASE_VISUALIZATIONS, MAP_BASE_VISUALIZATIONS, VISUALIZATION_ICONS, VISUALIZATION_ORDER } from './visual-catalog';
import { DEFAULT_DONUT_INNER_RADIUS } from './model';
import { activateLocalTerritoryCatalog, loadBdmoCatalog, loadEmissTerritoryCatalog, loadSettlementCatalog, loadWorldBankTerritoryCatalog } from './territories';
import { clearFormError, errorMessage, showFormError } from './status';
import { clearIndicatorDependentControls, currentIndicator, isBdmoIndicator, isEmissIndicator, isSettlementIndicator, isWorldBankIndicator, syncEmissCatalogPickerVisibility, updateAdvisor } from './indicators';
import { controls } from './controls';
import { currentLocale, localizedMonthLabel, localizedUnitLabel, localizedVisualizationLabel, setLabelText } from './locale';
import { escapeHtml, icon, refreshIcons } from './dom';
import { inspectorVisualization } from './workspace';
import { selectHasLoadingOption, selectedValues, selectedYearsFromForm, setSelectedValues, syncChoiceMirror } from './choice-lists';
import { state } from './state';
import { syncKpiIconControls } from './widget-appearance';
import { tr, trData } from '../ui-i18n';

export function updateSubmitState(): void {
  const hasIndicator = Boolean(currentIndicator());
  const hasVisualization = Boolean(controls.visualization.value);
  const hasTerritory = selectedValues(controls.territories).length > 0;
  const hasYear = selectedYearsFromForm().length > 0;
  const loading = selectHasLoadingOption(controls.territories)
    || selectHasLoadingOption(controls.years)
    || selectHasLoadingOption(controls.months);
  controls.submitWidget.disabled = !(hasIndicator && hasVisualization && hasTerritory && hasYear) || loading;
}

export function expandedAllowedVisualizations(indicator?: Indicator): VisualizationType[] {
  const base = new Set<VisualizationType>((indicator?.allowed_visualizations ?? VISUALIZATION_ORDER) as VisualizationType[]);
  if ([...base].some((visualization) => CARTESIAN_BASE_VISUALIZATIONS.has(visualization))) {
    base.add('pie');
    base.add('scatter');
    base.add('network');
    base.add('flow');
    base.add('small_multiples_bar');
  }
  if ([...base].some((visualization) => MAP_BASE_VISUALIZATIONS.has(visualization))) {
    base.add('flow_map');
    base.add('district_mini_maps');
  }
  return VISUALIZATION_ORDER.filter((visualization) => base.has(visualization));
}

export function updateVisualizationOptions(preferred?: VisualizationType): void {
  const indicator = currentIndicator();
  const allowed = expandedAllowedVisualizations(indicator);
  controls.visualization.innerHTML = allowed
    .map((visualization) => `<option value="${visualization}">${escapeHtml(localizedVisualizationLabel(visualization))}</option>`)
    .join('');
  const value = preferred && allowed.includes(preferred) ? preferred : (indicator?.default_visualization as VisualizationType);
  controls.visualization.value = allowed.includes(value) ? value : allowed[0] ?? 'line';
  renderVisualizationPalette(allowed, controls.visualization.value as VisualizationType);
  updateVisualizationSpecificControls();
  updateSubmitState();
}

export function renderVisualizationPalette(allowed: VisualizationType[], selected: VisualizationType): void {
  controls.visualizationPalette.innerHTML = VISUALIZATION_ORDER
    .map((visualization) => {
      const enabled = allowed.includes(visualization);
      const label = localizedVisualizationLabel(visualization);
      const selectedClass = visualization === selected ? ' is-active' : '';
      return `<button
        type="button"
        class="visualization-choice${selectedClass}"
        data-visualization-choice="${visualization}"
        data-tooltip="${escapeHtml(label)}"
        title="${escapeHtml(label)}"
        aria-label="${escapeHtml(label)}"
        aria-selected="${visualization === selected}"
        ${enabled ? '' : 'disabled'}
      >
        ${icon(VISUALIZATION_ICONS[visualization])}
        <span>${escapeHtml(label)}</span>
      </button>`;
    })
    .join('');
  refreshIcons(controls.visualizationPalette);
}

export function dimensionControls(): HTMLElement[] {
  return Array.from(controls.dimensionFields.querySelectorAll<HTMLElement>('[data-dimension-id]'));
}

export function selectedDimensionValues(control: HTMLElement): string[] {
  return Array.from(control.querySelectorAll<HTMLInputElement>('input[type="checkbox"]:checked'))
    .map((input) => input.value);
}

export function collectDimensionSelections(): Record<string, string[]> {
  return Object.fromEntries(
    dimensionControls().map((control) => [
      control.dataset.dimensionId ?? '',
      selectedDimensionValues(control),
    ]).filter(([id]) => Boolean(id)),
  );
}

export function isMapVisualization(visualization: VisualizationType): boolean {
  return visualization === 'choropleth' || visualization === 'map_symbols' || visualization === 'flow_map';
}

export function minimumWidgetHeight(visualization: VisualizationType): number {
  if (visualization === 'kpi') return 3;
  if (visualization === 'table') return 5;
  if (
    visualization === 'heatmap'
    || visualization === 'population_pyramid'
    || visualization === 'network'
    || visualization === 'flow'
    || visualization === 'small_multiples_bar'
    || visualization === 'district_mini_maps'
    || isMapVisualization(visualization)
  ) return 5;
  return 4;
}

export function dimensionInputs(dimensionId: string): HTMLInputElement[] {
  const control = controls.dimensionFields.querySelector<HTMLElement>(`[data-dimension-id="${CSS.escape(dimensionId)}"]`);
  return Array.from(control?.querySelectorAll<HTMLInputElement>('input[type="checkbox"]') ?? []);
}

export function setDimensionValues(dimensionId: string, values: string[]): void {
  const wanted = new Set(values);
  const inputs = dimensionInputs(dimensionId);
  inputs.forEach((input) => {
    input.checked = wanted.has(input.value);
  });
  const control = controls.dimensionFields.querySelector<HTMLElement>(`[data-dimension-id="${CSS.escape(dimensionId)}"]`);
  if (control) updateDimensionSummary(control);
}

export function ageBandStart(value: string): number | null {
  const text = value.trim().replaceAll(/[–—-]/g, '‒');
  const range = text.match(/^(\d{1,3})\s*‒\s*(\d{1,3})$/);
  if (range) {
    const start = Number(range[1]);
    const end = Number(range[2]);
    return Number.isFinite(start) && Number.isFinite(end) && start % 5 === 0 && end - start === 4 ? start : null;
  }
  const openEnded = text.match(/^(\d{2,3})\s*(?:\+|и\s+(?:более|старше))/i);
  if (openEnded) {
    const start = Number(openEnded[1]);
    return Number.isFinite(start) && start >= 100 ? start : null;
  }
  return null;
}

export function ensurePopulationPyramidDimensions(): void {
  const indicator = currentIndicator();
  if (!indicator?.source_metadata?.age_sex_indicator) return;
  const sexInputs = dimensionInputs('grup_2');
  const ageInputs = dimensionInputs('vozr');
  if (!sexInputs.length || !ageInputs.length) return;

  const selectedSex = sexInputs.filter((input) => input.checked).map((input) => input.value);
  const hasBothSexes = selectedSex.some((value) => value.toLocaleLowerCase('ru-RU').includes('муж'))
    && selectedSex.some((value) => value.toLocaleLowerCase('ru-RU').includes('жен'));
  if (!hasBothSexes) {
    setDimensionValues('grup_2', sexInputs
      .map((input) => input.value)
      .filter((value) => /муж|жен/i.test(value)));
  }

  const selectedAges = ageInputs.filter((input) => input.checked).map((input) => input.value);
  const selectedAgeBands = selectedAges.filter((value) => ageBandStart(value) != null);
  if (selectedAgeBands.length < 6) {
    const bands = ageInputs
      .map((input) => input.value)
      .filter((value) => ageBandStart(value) != null)
      .sort((a, b) => (ageBandStart(a) ?? 0) - (ageBandStart(b) ?? 0));
    if (bands.length) setDimensionValues('vozr', bands);
  }
}

export function ensureSingleDimensionsForEmissMap(visualization: VisualizationType): void {
  const indicator = currentIndicator();
  if (!isMapVisualization(visualization)
      || indicator?.source_type !== 'emiss'
      || indicator.source_metadata?.emiss_all_dimensions_supported !== true) {
    return;
  }
  state.dimensions.forEach((dimension) => {
    const inputs = dimensionInputs(dimension.id);
    if (!inputs.length) return;
    const selected = inputs.filter((input) => input.checked);
    if (selected.length === 1) return;
    const defaultValue = (dimension.default_values ?? []).find((value) => inputs.some((input) => input.value === value));
    const fallback = inputs.find((input) => /всего|итог|total|all/i.test(input.value))?.value
      ?? inputs[0]?.value;
    const value = defaultValue ?? fallback;
    if (value) setDimensionValues(dimension.id, [value]);
  });
}

export function currentIndicatorAllowsEmptyDimensions(visualization: VisualizationType): boolean {
  const indicator = currentIndicator();
  return Boolean(
    indicator?.source_type === 'emiss'
      && indicator.source_metadata?.emiss_all_dimensions_supported === true
      && !isMapVisualization(visualization),
  );
}

export function updateDimensionSummary(control: HTMLElement): void {
  const selected = selectedDimensionValues(control);
  const allowEmpty = currentIndicatorAllowsEmptyDimensions(controls.visualization.value as VisualizationType);
  const badge = control.querySelector<HTMLElement>('[data-dimension-summary]');
  const details = control.closest<HTMLDetailsElement>('details');
  if (badge) {
    badge.textContent = selected.length === 0
      ? allowEmpty
        ? 'все значения'
        : 'не выбрано'
      : selected.length === 1
        ? selected[0] ?? ''
        : `${selected.length} знач.`;
  }
  if (details) details.classList.toggle('has-no-selection', selected.length === 0 && !allowEmpty);
}

export function renderDimensions(
  dimensions: IndicatorDimension[],
  preferred: Record<string, string[]> = {},
): void {
  state.dimensions = dimensions;
  controls.dimensionFields.hidden = dimensions.length === 0;
  if (!dimensions.length) {
    controls.dimensionFields.innerHTML = '';
    return;
  }
  const needsChoice = dimensions.some((dimension) => !(dimension.default_values ?? []).length);
  const dimensionsHint = currentIndicatorAllowsEmptyDimensions(controls.visualization.value as VisualizationType)
    ? 'Если ничего не выбрано, дашборд покажет все значения этого разреза из страницы ЕМИСС.'
    : needsChoice
      ? 'Выберите значение каждого разреза по названию: итог автоматически не подставляется. «Без разреза» — строки источника без этого признака.'
      : 'Значения по умолчанию выбраны автоматически; изменяйте их только осознанно.';
  controls.dimensionFields.innerHTML = [
    `<div class="dimension-section-heading"><strong>Аналитические разрезы</strong><small>${escapeHtml(dimensionsHint)}</small></div>`,
    ...dimensions.map((dimension) => {
      const allowed = new Set(dimension.values.map((item) => item.value));
      const hasPreferred = Object.prototype.hasOwnProperty.call(preferred, dimension.id);
      const allowEmpty = currentIndicatorAllowsEmptyDimensions(controls.visualization.value as VisualizationType);
      const requested = hasPreferred
        ? preferred[dimension.id] ?? []
        : allowEmpty
          ? []
          : dimension.default_values ?? [];
      const selected = new Set(requested.filter((value) => allowed.has(value)));
      const options = dimension.values.map((item) => {
        const count = item.count == null ? '' : ` <span>${formatLocalizedNumber(item.count, currentLocale(), 0)} строк</span>`;
        return `<label class="dimension-option" data-dimension-option data-search="${escapeHtml(item.label.toLocaleLowerCase(currentLocale() === 'en' ? 'en-US' : 'ru-RU'))}">
          <input type="checkbox" value="${escapeHtml(item.value)}" ${selected.has(item.value) ? 'checked' : ''} />
          <span class="dimension-option-label">${escapeHtml(item.label)}</span>${count}
        </label>`;
      }).join('');
      const searchable = dimension.values.length > 12;
      return `<details class="dimension-control" data-dimension-id="${escapeHtml(dimension.id)}" ${dimensions.length <= 2 ? 'open' : ''}>
        <summary><span>${escapeHtml(dimension.name)}</span><span class="dimension-summary" data-dimension-summary></span></summary>
        <div class="dimension-control-body">
          ${searchable ? `<input class="dimension-search" data-dimension-search type="search" placeholder="Поиск по ${escapeHtml(dimension.name.toLocaleLowerCase(currentLocale() === 'en' ? 'en-US' : 'ru-RU'))}" />` : ''}
          <div class="dimension-actions">
            ${(dimension.default_values ?? []).length ? '<button type="button" class="text-button" data-dimension-action="default">По умолчанию</button>' : ''}
            <button type="button" class="text-button" data-dimension-action="clear">Снять выбор</button>
          </div>
          <div class="dimension-options" role="group" aria-label="${escapeHtml(dimension.name)}">${options}</div>
          <small class="field-help">Выбрано значений: <span data-dimension-count>${selected.size}</span> из ${dimension.values.length}.</small>
        </div>
      </details>`;
    }),
  ].join('');
  dimensionControls().forEach(updateDimensionSummary);
  if (controls.visualization.value === 'population_pyramid') {
    ensurePopulationPyramidDimensions();
  }
}

export async function loadDimensions(
  preferred: Record<string, string[]> = {},
  token = state.indicatorRequestToken,
  indicatorId = currentIndicator()?.id,
): Promise<void> {
  const indicator = currentIndicator();
  if (!indicator) return;
  controls.dimensionFields.hidden = false;
  controls.dimensionFields.innerHTML = '<div class="dimension-loading">Загрузка аналитических разрезов…</div>';
  try {
    const dimensions = await api.dimensions(indicator.id, currentLocale());
    if (token !== state.indicatorRequestToken || controls.indicator.value !== indicatorId) return;
    renderDimensions(dimensions, preferred);
  } catch (error) {
    if (token !== state.indicatorRequestToken || controls.indicator.value !== indicatorId) return;
    state.dimensions = [];
    controls.dimensionFields.innerHTML = '';
    controls.dimensionFields.hidden = true;
    showFormError(`Не удалось получить аналитические разрезы: ${errorMessage(error)}`);
  }
}

export function validateDimensionSelections(visualization: VisualizationType): Record<string, string[]> {
  const selected = collectDimensionSelections();
  const allowEmpty = currentIndicatorAllowsEmptyDimensions(visualization);
  state.dimensions.forEach((dimension) => {
    if (dimension.required && !(selected[dimension.id]?.length) && !allowEmpty) {
      throw new Error(`Выберите хотя бы одно значение разреза «${dimension.name}».`);
    }
  });
  return selected;
}

export async function loadPeriods(
  preferredYears?: number[],
  preferredMonths?: number[],
  token = state.indicatorRequestToken,
  indicatorId = currentIndicator()?.id,
): Promise<void> {
  const indicator = currentIndicator();
  if (!indicator) return;
  controls.years.innerHTML = '<option disabled>Загрузка…</option>';
  syncChoiceMirror(controls.years);
  updateSubmitState();
  try {
    const periods = await api.periods(indicator.id, currentLocale());
    if (token !== state.indicatorRequestToken || controls.indicator.value !== indicatorId) return;
    const years = [...periods.years].sort((a, b) => b - a);
    controls.years.innerHTML = years
      .map((year) => `<option value="${escapeHtml(String(year))}">${escapeHtml(String(year))}</option>`)
      .join('');
    const selectedYears = preferredYears?.length ? preferredYears : years.slice(0, Math.min(3, years.length));
    setSelectedValues(controls.years, selectedYears);
    controls.yearSelectionHint.textContent = preferredYears?.length
      ? ''
      : `Автовыбор: последние ${selectedYears.length} из ${years.length} доступных лет.`;

    const availableMonths = periods.months.length ? periods.months : Array.from({ length: 12 }, (_, index) => index + 1);
    controls.months.innerHTML = availableMonths
      .sort((a, b) => a - b)
      .map((month) => `<option value="${month}">${escapeHtml(localizedMonthLabel(month))}</option>`)
      .join('');
    setSelectedValues(controls.months, preferredMonths ?? []);
    controls.monthField.hidden = indicator.frequency !== 'monthly';
    const periodKind = periods.period_kind ?? indicator.period_kind ?? 'unknown';
    const periodLabel = periods.period_label ?? indicator.period_label;
    const messages: Record<string, string> = {
      fixed_date: 'Фиксированная дата наблюдения',
      stock: 'Моментный показатель',
      flow: 'Показатель за период',
      unknown: 'Периодизация источника',
    };
    controls.periodSemantics.hidden = !(periodLabel || periods.note);
    controls.periodSemantics.innerHTML = periodLabel || periods.note
      ? `<strong>${escapeHtml(tr(messages[periodKind] ?? messages.unknown ?? ''))}:</strong> ${escapeHtml(trData(periodLabel ?? ''))}${periods.note ? `<small>${escapeHtml(trData(periods.note))}</small>` : ''}`
      : '';
    controls.periodSemantics.dataset.kind = periodKind;
    updateAdvisor();
    updateSubmitState();
  } catch (error) {
    if (token !== state.indicatorRequestToken || controls.indicator.value !== indicatorId) return;
    controls.years.innerHTML = '';
    controls.yearSelectionHint.textContent = '';
    syncChoiceMirror(controls.years);
    showFormError(`Не удалось получить доступные периоды: ${errorMessage(error)}`);
    updateSubmitState();
  }
}

export async function updateIndicatorControls(
  preferredVisualization?: VisualizationType,
  preferredYears?: number[],
  preferredMonths?: number[],
  preferredDimensions: Record<string, string[]> = {},
  preferredTerritories: string[] = [],
): Promise<void> {
  const token = ++state.indicatorRequestToken;
  syncEmissCatalogPickerVisibility();
  const indicator = currentIndicator();
  if (!indicator) {
    updateVisualizationOptions(preferredVisualization);
    clearIndicatorDependentControls(
      controls.source.value === 'emiss'
        ? 'Сначала найдите показатель в глобальном каталоге ЕМИСС и подключите его.'
        : controls.source.value === 'worldbank'
          ? 'Сначала найдите показатель в каталоге международной статистики и подключите его.'
        : '',
    );
    return;
  }
  if (indicator) {
    const unitLabel = localizedUnitLabel(indicator.unit);
    const fullDescription = `${unitLabel || indicator.unit}; ${indicator.source_name}. ${indicator.description ?? ''}`.trim();
    controls.indicatorDescription.textContent = unitLabel ? `${t(currentLocale(), 'unit')}: ${unitLabel}` : '';
    controls.indicatorDescription.title = fullDescription;
  } else {
    controls.indicatorDescription.textContent = '';
    controls.indicatorDescription.removeAttribute('title');
  }
  clearFormError();
  updateVisualizationOptions(preferredVisualization);
  await loadPeriods(preferredYears, preferredMonths, token, indicator.id);
  if (token !== state.indicatorRequestToken || controls.indicator.value !== indicator.id) return;
  await loadDimensions(preferredDimensions, token, indicator.id);
  if (token !== state.indicatorRequestToken || controls.indicator.value !== indicator.id) return;
  const settlements = isSettlementIndicator(indicator);
  controls.territorySearchField.hidden = !settlements;
  if (!settlements) controls.territorySearch.value = '';
  if (isBdmoIndicator(indicator)) {
    await loadBdmoCatalog(preferredTerritories);
  } else if (settlements) {
    await loadSettlementCatalog(preferredTerritories);
  } else if (isEmissIndicator(indicator)) {
    await loadEmissTerritoryCatalog(preferredTerritories);
  } else if (isWorldBankIndicator(indicator)) {
    await loadWorldBankTerritoryCatalog(preferredTerritories);
  } else {
    activateLocalTerritoryCatalog(preferredTerritories);
  }
  updateAdvisor();
  updateSubmitState();
}

export function updateVisualizationSpecificControls(): void {
  const visualization = inspectorVisualization();
  const isMap = isMapVisualization(visualization);
  const isSnapshot = isSnapshotVisualization(visualization);
  const cartesian = visualization === 'line'
    || visualization === 'bar'
    || visualization === 'area'
    || visualization === 'normalized_bar'
    || visualization === 'scatter'
    || visualization === 'small_multiples_bar';
  const lineLike = visualization === 'line' || visualization === 'area';
  const ranked = visualization === 'bar' || visualization === 'pie' || visualization === 'small_multiples_bar';
  const hasLegend = !['kpi', 'table', 'histogram'].includes(visualization);

  const setVisible = (control: HTMLElement, visible: boolean): void => {
    const host = control.closest<HTMLElement>('.field, .check-field');
    if (host) host.hidden = !visible;
  };
  setVisible(controls.orientation, visualization === 'bar' || visualization === 'normalized_bar');
  setVisible(controls.sortOrder, ranked);
  setVisible(controls.topN, ranked || lineLike);
  setVisible(controls.axisScale, cartesian && visualization !== 'normalized_bar');
  setVisible(controls.includeZero, cartesian && visualization !== 'normalized_bar');
  setVisible(controls.dataZoom, cartesian || visualization === 'heatmap');
  setVisible(controls.showPoints, lineLike);
  setVisible(controls.smooth, lineLike);
  setVisible(controls.palette, visualization !== 'table');
  setVisible(controls.labels, !['kpi', 'table'].includes(visualization));
  setVisible(controls.reverse, visualization !== 'table');
  setVisible(controls.legendPosition, hasLegend);
  setVisible(controls.legend, hasLegend);
  setVisible(controls.patterns, hasLegend && !isMap);
  controls.typeSpecificSettings.hidden = false;
  if (visualization === 'kpi') controls.typeSpecificSettings.open = true;
  const hasAdvancedSettings = ['line', 'bar', 'area', 'normalized_bar', 'scatter', 'small_multiples_bar', 'heatmap', 'pie', 'population_pyramid', 'network', 'flow'].includes(visualization);
  controls.advancedSettings.hidden = !hasAdvancedSettings;
  if (visualization === 'population_pyramid') ensurePopulationPyramidDimensions();
  ensureSingleDimensionsForEmissMap(visualization);

  controls.mapSettings.classList.toggle('is-relevant', isMap || isSnapshot);
  controls.mapSettings.hidden = !(isMap || isSnapshot);
  controls.mapSettings.open = isMap || (isSnapshot && controls.mapSettings.open);
  const summary = controls.mapSettings.querySelector('summary');
  if (summary) summary.textContent = isMap ? t(currentLocale(), 'mapSettings') : t(currentLocale(), 'periodDisplay');
  [
    controls.classification,
    controls.classCount,
    controls.manualBreaks,
    controls.nullColor,
    controls.mapScaleMode,
    controls.mapLegendMode,
    controls.mapBasemap,
    controls.mapFillOpacity,
    controls.mapOutlineColor,
    controls.mapOutlineWidth,
    controls.mapBasemapOpacity,
  ]
    .forEach((control) => setVisible(control, isMap));
  Array.from(document.querySelectorAll<HTMLElement>('.line-area-setting')).forEach((control) =>
    setVisible(control, visualization === 'line' || visualization === 'area'),
  );
  Array.from(document.querySelectorAll<HTMLElement>('.axis-limit-setting')).forEach((control) =>
    setVisible(control, ['line', 'area', 'bar', 'normalized_bar', 'scatter', 'histogram', 'small_multiples_bar'].includes(visualization)),
  );
  Array.from(document.querySelectorAll<HTMLElement>('.axis-x-limit-setting')).forEach((control) =>
    setVisible(control, visualization === 'scatter'),
  );
  const locale = currentLocale();
  setLabelText(
    controls.axisMin,
    visualization === 'scatter'
      ? (locale === 'en' ? 'Y minimum' : 'Минимум Y')
      : (locale === 'en' ? 'Value axis minimum' : 'Минимум оси значений'),
  );
  setLabelText(
    controls.axisMax,
    visualization === 'scatter'
      ? (locale === 'en' ? 'Y maximum' : 'Максимум Y')
      : (locale === 'en' ? 'Value axis maximum' : 'Максимум оси значений'),
  );
  Array.from(document.querySelectorAll<HTMLElement>('.bar-setting')).forEach((control) =>
    setVisible(control, visualization === 'bar' || visualization === 'normalized_bar' || visualization === 'small_multiples_bar'),
  );
  Array.from(document.querySelectorAll<HTMLElement>('.bar-pie-setting')).forEach((control) =>
    setVisible(control, visualization === 'bar' || visualization === 'normalized_bar' || visualization === 'pie'),
  );
  Array.from(document.querySelectorAll<HTMLElement>('.pie-setting')).forEach((control) =>
    setVisible(control, visualization === 'pie'),
  );
  if (visualization === 'pie' && controls.pieVariant.value === 'donut' && Number(controls.pieInnerRadius.value) <= 0) {
    controls.pieInnerRadius.value = String(DEFAULT_DONUT_INNER_RADIUS);
  }
  Array.from(document.querySelectorAll<HTMLElement>('.pie-radius-setting')).forEach((control) =>
    setVisible(control, visualization === 'pie' && controls.pieVariant.value === 'donut'),
  );
  controls.pieInnerRadius.disabled = visualization !== 'pie' || controls.pieVariant.value !== 'donut';
  Array.from(document.querySelectorAll<HTMLElement>('.heatmap-setting')).forEach((control) =>
    setVisible(control, visualization === 'heatmap'),
  );
  Array.from(document.querySelectorAll<HTMLElement>('.pyramid-setting')).forEach((control) =>
    setVisible(control, visualization === 'population_pyramid'),
  );
  Array.from(document.querySelectorAll<HTMLElement>('.kpi-setting')).forEach((control) =>
    setVisible(control, visualization === 'kpi'),
  );
  if (visualization === 'kpi') syncKpiIconControls();
  Array.from(document.querySelectorAll<HTMLElement>('.table-setting')).forEach((control) =>
    setVisible(control, visualization === 'table'),
  );
  Array.from(document.querySelectorAll<HTMLElement>('.symbol-map-setting')).forEach((control) =>
    setVisible(control, visualization === 'map_symbols' || visualization === 'flow_map'),
  );
  setVisible(controls.timeControl, isSnapshot);
  updateClassificationControls();
  updateAdvisor();
}

export function updateClassificationControls(): void {
  const visualization = inspectorVisualization();
  const isMap = isMapVisualization(visualization);
  controls.manualBreaksField.hidden = !isMap || controls.classification.value !== 'manual';
  const fixed = isMap && controls.mapScaleMode.value === 'fixed';
  controls.mapFixedFields.forEach((field) => {
    field.hidden = !fixed;
  });
}

export function updateConditionControls(): void {
  const enabled = controls.conditionOperator.value !== 'none';
  controls.conditionValue.disabled = !enabled;
  controls.conditionColor.disabled = !enabled;
}

export function parseManualBreaks(value: string): number[] {
  if (!value.trim()) return [];
  const parsed = value
    .split(/[;,\s]+/)
    .filter(Boolean)
    .map((item) => Number(item.replace(',', '.')));
  if (parsed.some((item) => !Number.isFinite(item))) {
    throw new Error('Границы классов должны быть числами, разделёнными точкой с запятой.');
  }
  return [...new Set(parsed)].sort((a, b) => a - b);
}
